import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const EG_BODY_SCROLL_LOCK_KEY = '__egBodyScrollLockState';
const EG_MOBILE_DRAWER_MEDIA = '(max-width: 989px)';

function getEgBodyScrollLockState() {
  const root = typeof globalThis !== 'undefined' ? globalThis : window;

  if (root[EG_BODY_SCROLL_LOCK_KEY]) {
    return root[EG_BODY_SCROLL_LOCK_KEY];
  }

  const state = {
    owners: new Set(),
    previousScrollY: 0,
    previousStyles: {
      htmlOverflow: '',
      htmlOverscrollBehavior: '',
      bodyWidth: '',
      bodyPosition: '',
      bodyTop: '',
      bodyLeft: '',
      bodyRight: '',
      bodyOverflow: '',
      bodyOverscrollBehavior: '',
      bodyPaddingRight: '',
    },
    previousBodyPaddingRight: '',
    appliedScrollbarCompensation: false,
  };

  root[EG_BODY_SCROLL_LOCK_KEY] = state;
  return state;
}

function lockEgBodyScroll(owner, dialog) {
  if (!owner || !dialog) return;

  const state = getEgBodyScrollLockState();
  if (state.owners.has(owner)) return;

  state.owners.add(owner);

  if (state.owners.size > 1) {
    return;
  }

  const docEl = document.documentElement;
  const body = document.body;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const skipScrollbarComp = dialog.hasAttribute('data-eg-no-scrollbar-compensation');
  const scrollbarWidth = Math.max(0, window.innerWidth - docEl.clientWidth);

  state.previousScrollY = scrollY;
  state.previousBodyPaddingRight = body.style.paddingRight || '';
  state.previousStyles = {
    htmlOverflow: docEl.style.overflow || '',
    htmlOverscrollBehavior: docEl.style.overscrollBehavior || '',
    bodyWidth: body.style.width || '',
    bodyPosition: body.style.position || '',
    bodyTop: body.style.top || '',
    bodyLeft: body.style.left || '',
    bodyRight: body.style.right || '',
    bodyOverflow: body.style.overflow || '',
    bodyOverscrollBehavior: body.style.overscrollBehavior || '',
    bodyPaddingRight: body.style.paddingRight || '',
  };

  state.appliedScrollbarCompensation = !skipScrollbarComp && scrollbarWidth > 0;

  if (state.appliedScrollbarCompensation) {
    const currentPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
  }

  docEl.style.overflow = 'hidden';
  docEl.style.overscrollBehavior = 'none';

  body.style.width = '100%';
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.overflow = 'hidden';
  body.style.overscrollBehavior = 'none';
}

function unlockEgBodyScroll(owner) {
  const state = getEgBodyScrollLockState();
  if (!state.owners.has(owner)) return;

  state.owners.delete(owner);

  if (state.owners.size > 0) {
    return;
  }

  const docEl = document.documentElement;
  const body = document.body;

  docEl.style.overflow = state.previousStyles.htmlOverflow;
  docEl.style.overscrollBehavior = state.previousStyles.htmlOverscrollBehavior;

  body.style.width = state.previousStyles.bodyWidth;
  body.style.position = state.previousStyles.bodyPosition;
  body.style.top = state.previousStyles.bodyTop;
  body.style.left = state.previousStyles.bodyLeft;
  body.style.right = state.previousStyles.bodyRight;
  body.style.overflow = state.previousStyles.bodyOverflow;
  body.style.overscrollBehavior = state.previousStyles.bodyOverscrollBehavior;

  if (state.appliedScrollbarCompensation) {
    body.style.paddingRight = state.previousBodyPaddingRight;
    state.appliedScrollbarCompensation = false;
  } else {
    body.style.paddingRight = state.previousStyles.bodyPaddingRight;
  }

  const restoreY = state.previousScrollY || 0;

  try {
    window.scrollTo({ top: restoreY, behavior: 'auto' });
  } catch (_) {
    try {
      window.scrollTo(0, restoreY);
    } catch (_) {}
  }
}

function shouldLockEgBodyScroll(dialog) {
  if (!dialog) return false;
  if (dialog.hasAttribute('data-eg-force-page-scroll-lock')) return true;

  try {
    return window.matchMedia(EG_MOBILE_DRAWER_MEDIA).matches;
  } catch (_) {
    return window.innerWidth <= 989;
  }
}

function waitForTransition(element, fallback = 260) {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }

    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      element.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      resolve();
    };

    const onEnd = (event) => {
      if (event.target !== element) return;
      finish();
    };

    const timer = setTimeout(finish, fallback);
    element.addEventListener('transitionend', onEnd);
  });
}

function waitForCartOpenFinish(drawer, fallback = 320) {
  return new Promise((resolve) => {
    const dialog =
      drawer?.refs?.dialog ||
      drawer?.querySelector?.('dialog[ref="dialog"], dialog.cart-drawer__dialog');

    if (!dialog) {
      resolve();
      return;
    }

    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      resolve();
    };

    const onEnd = (event) => {
      if (event.target !== dialog) return;
      finish();
    };

    const timer = setTimeout(finish, fallback);
    dialog.addEventListener('transitionend', onEnd);
  });
}

function hideMobileMenuUnderCartNow(header, mobileDrawer, burger) {
  if (!header) return;

  header.classList.remove('is-drawer-open', 'is-drawer-dragging');
  header.style.removeProperty('--eg-m-drag-x');
  header.style.removeProperty('--eg-m-backdrop-opacity');

  if (mobileDrawer) {
    mobileDrawer.hidden = true;
  }

  if (burger) {
    burger.setAttribute('aria-expanded', 'false');
  }
}

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('eg:cart:added', this.#handleEgAdded);
    document.addEventListener('click', this.#handleNativeCloseClick, true);
    window.addEventListener('resize', this.#handleWindowResize, { passive: true });

    this.#bindWheelGuard();
    this.#bindDragScroll();
    this.#bindMobileSwipe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('eg:cart:added', this.#handleEgAdded);
    document.removeEventListener('click', this.#handleNativeCloseClick, true);
    window.removeEventListener('resize', this.#handleWindowResize);

    this.#unbindWheelGuard();
    this.#unbindDragScroll();
    this.#unbindMobileSwipe();
    this.#stopDragging();
    this.#stopInertia();
    this.#resetMobileSwipe();

    if (this.#pageScrollLocked) {
      unlockEgBodyScroll(this);
      this.#pageScrollLocked = false;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) this.open();
  };

  #handleEgAdded = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    try {
      document.dispatchEvent(
        new CustomEvent(CartAddEvent.eventName, { detail: event?.detail || {} })
      );
    } catch (_) {}

    this.open();
  };

  #handleNativeCloseClick = (event) => {
    const btn = event.target?.closest?.('[on\\:click="/close"]');
    if (!btn) return;
    if (!this.contains(btn)) return;

    event.preventDefault();
    event.stopPropagation();

    this.closeDialog();
  };

  #closeOtherDialogs() {
    document.querySelectorAll('dialog[open].dialog-modal').forEach((dialog) => {
      const ownDialog = this.refs?.dialog;
      if (!dialog || dialog === ownDialog) return;

      const owner = dialog.closest(
        'cart-drawer-component, wishlist-drawer-component, account-drawer-component, dialog-component'
      );

      try {
        if (owner && typeof owner.close === 'function') {
          owner.close();
          return;
        }
      } catch (_) {}

      try {
        dialog.close();
      } catch (_) {}
    });
  }

  async showDialog() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open || this.#isTransitioning) return;

    this.#isTransitioning = true;
    this.#stopInertia();
    this.#closeOtherDialogs();

    try {
      if (shouldLockEgBodyScroll(dialog)) {
        lockEgBodyScroll(this, dialog);
        this.#pageScrollLocked = true;
      } else {
        this.#pageScrollLocked = false;
      }

      try {
        dialog.showModal();
      } catch (_) {
        this.#closeOtherDialogs();
        dialog.showModal();
      }

      this.dispatchEvent(
        new CustomEvent('dialog:open', { bubbles: true, composed: true })
      );

      this.addEventListener('click', this.#handleOutsideClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      this.#bindWheelGuard();
      this.#bindDragScroll();
      this.#bindMobileSwipe();
      this.#resetMobileSwipe();

dialog.dataset.egState = 'opening';

await new Promise((resolve) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(resolve);
  });
});

dialog.dataset.egState = 'open';

      await waitForTransition(dialog, this.#openFallbackMs);
    } catch (_) {
      try {
        if (dialog.open) dialog.close();
      } catch (_) {}

      if (this.#pageScrollLocked) {
        unlockEgBodyScroll(this);
        this.#pageScrollLocked = false;
      }

      delete dialog.dataset.egState;
      this.#resetMobileSwipe();
    } finally {
      this.#isTransitioning = false;
    }
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open || this.#isTransitioning) return;

    this.#isTransitioning = true;

    this.dispatchEvent(
      new CustomEvent('dialog:closing', { bubbles: true, composed: true })
    );

    this.removeEventListener('click', this.#handleOutsideClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.#stopDragging();
    this.#stopInertia();

    try {
      dialog.dataset.egState = 'closing';
      await waitForTransition(dialog, this.#closeFallbackMs);
    } catch (_) {}

    try {
      if (dialog.open) dialog.close();
    } catch (_) {}

    if (this.#pageScrollLocked) {
      unlockEgBodyScroll(this);
      this.#pageScrollLocked = false;
    }

    delete dialog.dataset.egState;
    this.#resetMobileSwipe();

    this.dispatchEvent(
      new CustomEvent('dialog:close', { bubbles: true, composed: true })
    );

    this.#isTransitioning = false;
  };

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #ignoreOutsideClickUntil = 0;
  #isTransitioning = false;
  #openFallbackMs = 260;
  #closeFallbackMs = 280;
  #pageScrollLocked = false;

  #handleWindowResize = () => {
    this.#syncPageScrollLock();
  };

  #syncPageScrollLock() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    const shouldLock = shouldLockEgBodyScroll(dialog);

    if (shouldLock && !this.#pageScrollLocked) {
      lockEgBodyScroll(this, dialog);
      this.#pageScrollLocked = true;
      return;
    }

    if (!shouldLock && this.#pageScrollLocked) {
      unlockEgBodyScroll(this);
      this.#pageScrollLocked = false;
    }

    if (!this.#isMobileDrawer()) {
      this.#resetMobileSwipe();
    }
  }

  #isMobileDrawer() {
    try {
      return window.matchMedia(EG_MOBILE_DRAWER_MEDIA).matches;
    } catch (_) {
      return window.innerWidth <= 989;
    }
  }

  #now() {
    return typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
  }

  #handleOutsideClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    if (this.#dragging) return;
    if (this.#mobileSwipeActive) return;
    if (this.#now() < this.#ignoreOutsideClickUntil) return;

    const rect = dialog.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    const clickedOutside =
      x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

    if (clickedOutside) this.closeDialog();
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  #mobileSwipeBound = false;
  #mobileSwipeActive = false;
  #mobileSwipeLocked = null;
  #mobileSwipeStartX = 0;
  #mobileSwipeStartY = 0;
  #mobileSwipeX = 0;

  #isInteractiveForDrawerSwipe(target) {
    if (!target || target.nodeType !== Node.ELEMENT_NODE) return false;

    return !!target.closest(
      [
        'a',
        'button',
        'input',
        'select',
        'textarea',
        'label',
        'summary',
        '[role="button"]',
        '[on\\:click="/close"]',
        '[data-no-drag]',
      ].join(', ')
    );
  }

  #getSwipeWidth() {
    const { dialog } = this.refs;
    if (!dialog) return Math.max(window.innerWidth || 0, 1);

    const rect = dialog.getBoundingClientRect();
    return Math.max(rect?.width || window.innerWidth || 0, 1);
  }

  #setMobileSwipeVisual(x) {
    const { dialog } = this.refs;
    if (!dialog) return;

    const width = Math.max(this.#getSwipeWidth(), 1);
    const nextX = Math.max(-width, Math.min(x, 0));

    this.#mobileSwipeX = nextX;
    dialog.dataset.egMobileDragging = 'true';
    dialog.style.setProperty('--eg-cart-mobile-drag-x', `${nextX}px`);
  }

  #enableMobileDragMode() {
    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.dataset.egMobileDragging = 'true';
    dialog.style.setProperty('transition', 'none', 'important');
  }

  #disableMobileDragMode() {
    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.style.removeProperty('transition');
  }

  #resetMobileSwipe() {
    const { dialog } = this.refs;

    this.#mobileSwipeActive = false;
    this.#mobileSwipeLocked = null;
    this.#mobileSwipeStartX = 0;
    this.#mobileSwipeStartY = 0;
    this.#mobileSwipeX = 0;

    if (!dialog) return;

    this.#disableMobileDragMode();
    delete dialog.dataset.egMobileDragging;
    dialog.style.removeProperty('--eg-cart-mobile-drag-x');
  }

  #finishMobileSwipeSnapBack() {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#disableMobileDragMode();
    this.#setMobileSwipeVisual(0);

    const cleanup = () => {
      if (!dialog.open) return;
      this.#resetMobileSwipe();
    };

    window.setTimeout(cleanup, 260);
  }

  #onMobileSwipeStart = (event) => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;
    if (!this.#isMobileDrawer()) return;
    if (!event.touches || event.touches.length !== 1) return;
    if (this.#dragging) return;
    if (this.#isInteractiveForDrawerSwipe(event.target)) return;

    const touch = event.touches[0];

    this.#mobileSwipeActive = true;
    this.#mobileSwipeLocked = null;
    this.#mobileSwipeStartX = touch.clientX;
    this.#mobileSwipeStartY = touch.clientY;
    this.#mobileSwipeX = 0;
  };

  #onMobileSwipeMove = (event) => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;
    if (!this.#mobileSwipeActive) return;
    if (!this.#isMobileDrawer()) return;
    if (!event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const dx = touch.clientX - this.#mobileSwipeStartX;
    const dy = touch.clientY - this.#mobileSwipeStartY;

    if (this.#mobileSwipeLocked == null) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;

      if (dx < -10 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        this.#mobileSwipeLocked = 'x';
        this.#enableMobileDragMode();
      } else if (Math.abs(dy) > Math.abs(dx)) {
        this.#mobileSwipeLocked = 'y';
        return;
      } else {
        return;
      }
    }

    if (this.#mobileSwipeLocked !== 'x') return;

    if (event.cancelable) {
      event.preventDefault();
    }

    this.#setMobileSwipeVisual(dx);
  };

  #onMobileSwipeEnd = () => {
    const { dialog } = this.refs;
    if (!dialog) return;
    if (!this.#mobileSwipeActive) return;

    const width = Math.max(this.#getSwipeWidth(), 1);
    const draggedEnough = Math.abs(this.#mobileSwipeX) > Math.max(96, width * 0.22);

    this.#mobileSwipeActive = false;

    if (this.#mobileSwipeLocked === 'x' && draggedEnough) {
      this.#disableMobileDragMode();
      this.#setMobileSwipeVisual(-width);
      this.#ignoreOutsideClickUntil = this.#now() + 450;
      this.closeDialog();
      return;
    }

    if (this.#mobileSwipeLocked === 'x') {
      this.#finishMobileSwipeSnapBack();
      return;
    }

    this.#resetMobileSwipe();
  };

  #bindMobileSwipe() {
    const { dialog } = this.refs;
    if (!dialog || this.#mobileSwipeBound) return;

    dialog.addEventListener('touchstart', this.#onMobileSwipeStart, { passive: true });
    dialog.addEventListener('touchmove', this.#onMobileSwipeMove, { passive: false });
    dialog.addEventListener('touchend', this.#onMobileSwipeEnd, { passive: true });
    dialog.addEventListener('touchcancel', this.#onMobileSwipeEnd, { passive: true });

    this.#mobileSwipeBound = true;
  }

  #unbindMobileSwipe() {
    const { dialog } = this.refs;
    if (!dialog || !this.#mobileSwipeBound) return;

    try {
      dialog.removeEventListener('touchstart', this.#onMobileSwipeStart);
      dialog.removeEventListener('touchmove', this.#onMobileSwipeMove);
      dialog.removeEventListener('touchend', this.#onMobileSwipeEnd);
      dialog.removeEventListener('touchcancel', this.#onMobileSwipeEnd);
    } catch (_) {}

    this.#mobileSwipeBound = false;
  }

  #wheelBound = false;
  #wheelEl = null;

  #getScrollContainer() {
    return (
      this.querySelector('[data-eg-cart-items-scroll]') ||
      this.querySelector('scroll-hint.cart-drawer__items') ||
      this.querySelector('.cart-drawer__items') ||
      this.querySelector('.cart-drawer__content')
    );
  }

  #isWheelOverScroller(event, scroller) {
    const hasPoint =
      typeof event.clientX === 'number' && typeof event.clientY === 'number';

    if (hasPoint) {
      const el = document.elementFromPoint(event.clientX, event.clientY);
      if (el && scroller.contains(el)) return true;
    }

    return Boolean(event.target && scroller.contains(event.target));
  }

  #onWheelCapture = (event) => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    const scroller = this.#getScrollContainer();
    if (!scroller) return;
    if (!this.#isWheelOverScroller(event, scroller)) return;

    const dy = event.deltaY || 0;
    const dx = event.deltaX || 0;
    if (dx === 0 && dy === 0) return;

    const canScrollY = scroller.scrollHeight > scroller.clientHeight + 1;
    if (!canScrollY) return;

    this.#stopInertia();

    event.preventDefault();
    event.stopPropagation();

    scroller.scrollLeft = 0;
    if (dy) scroller.scrollTop += dy;
  };

  #bindWheelGuard() {
    const { dialog } = this.refs;
    if (!dialog || this.#wheelBound) return;

    dialog.addEventListener('wheel', this.#onWheelCapture, {
      capture: true,
      passive: false,
    });

    this.#wheelEl = dialog;
    this.#wheelBound = true;
  }

  #unbindWheelGuard() {
    if (!this.#wheelEl || !this.#wheelBound) return;

    try {
      this.#wheelEl.removeEventListener('wheel', this.#onWheelCapture, { capture: true });
    } catch (_) {}

    this.#wheelEl = null;
    this.#wheelBound = false;
  }

  #dragBound = false;
  #dragging = false;
  #dragPointerId = null;
  #dragStartY = 0;
  #dragStartScrollTop = 0;
  #dragMoved = false;
  #dragLastY = 0;
  #dragLastTime = 0;
  #inertiaVelocityY = 0;
  #inertiaRaf = 0;

  #isInteractiveTarget(target) {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest(
        'a,button,input,textarea,select,option,label,summary,[role="button"],[contenteditable="true"],[data-no-drag]'
      )
    );
  }

  #stopInertia() {
    if (this.#inertiaRaf) {
      cancelAnimationFrame(this.#inertiaRaf);
      this.#inertiaRaf = 0;
    }

    this.#inertiaVelocityY = 0;
  }

  #startInertia() {
    const scroller = this.#getScrollContainer();
    if (!scroller) return;

    let vy = this.#inertiaVelocityY;

    const minVelocity = 0.03;
    if (Math.abs(vy) < minVelocity) {
      this.#stopInertia();
      return;
    }

    this.#stopInertia();

    let lastTime = this.#now();

    const tick = (now) => {
      const dt = Math.max(1, Math.min(34, now - lastTime));
      lastTime = now;

      const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);

      let nextTop = scroller.scrollTop + vy * dt;

      if (nextTop <= 0) {
        nextTop = 0;
        vy = 0;
      } else if (nextTop >= maxTop) {
        nextTop = maxTop;
        vy = 0;
      }

      scroller.scrollLeft = 0;
      scroller.scrollTop = nextTop;

      const decay = Math.pow(0.88, dt / 16.6667);
      vy *= decay;

      if (Math.abs(vy) < minVelocity) vy = 0;

      this.#inertiaVelocityY = vy;

      if (vy === 0) {
        this.#stopInertia();
        return;
      }

      this.#inertiaRaf = requestAnimationFrame(tick);
    };

    this.#inertiaRaf = requestAnimationFrame(tick);
  }

  #onPointerDown = (event) => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    const scroller = this.#getScrollContainer();
    if (!scroller) return;

    if (event.pointerType !== 'mouse') return;
    if (event.button !== 0) return;
    if (!scroller.contains(event.target)) return;
    if (this.#isInteractiveTarget(event.target)) return;

    const canScrollY = scroller.scrollHeight > scroller.clientHeight + 1;
    if (!canScrollY) return;

    this.#stopInertia();

    this.#dragging = true;
    this.#dragMoved = false;
    this.#dragPointerId = event.pointerId;
    this.#dragStartY = event.clientY;
    this.#dragStartScrollTop = scroller.scrollTop;
    this.#dragLastY = event.clientY;
    this.#dragLastTime = this.#now();
    this.#inertiaVelocityY = 0;

    try {
      scroller.setPointerCapture(event.pointerId);
    } catch (_) {}

    dialog.classList.add('eg-cart-dragging');

    event.preventDefault();
  };

  #onPointerMove = (event) => {
    if (!this.#dragging) return;

    const scroller = this.#getScrollContainer();
    if (!scroller) return;

    if (this.#dragPointerId != null && event.pointerId !== this.#dragPointerId) return;

    const dy = event.clientY - this.#dragStartY;

    if (!this.#dragMoved && Math.abs(dy) > 3) {
      this.#dragMoved = true;
    }

    scroller.scrollLeft = 0;
    scroller.scrollTop = this.#dragStartScrollTop - dy;

    const now = this.#now();
    const dt = Math.max(1, now - this.#dragLastTime);
    const deltaY = event.clientY - this.#dragLastY;

    const instantVy = -deltaY / dt;
    this.#inertiaVelocityY = this.#inertiaVelocityY * 0.78 + instantVy * 0.22;

    this.#dragLastY = event.clientY;
    this.#dragLastTime = now;

    event.preventDefault();
  };

  #onPointerUpOrCancel = (event) => {
    if (!this.#dragging) return;
    if (this.#dragPointerId != null && event.pointerId !== this.#dragPointerId) return;

    const shouldStartInertia =
      this.#dragMoved && Math.abs(this.#inertiaVelocityY) > 0.03;

    if (this.#dragMoved) {
      this.#ignoreOutsideClickUntil = this.#now() + 450;
    }

    this.#stopDragging();

    if (shouldStartInertia) {
      this.#startInertia();
    } else {
      this.#stopInertia();
    }
  };

  #stopDragging() {
    const { dialog } = this.refs;

    this.#dragging = false;
    this.#dragPointerId = null;
    this.#dragMoved = false;
    this.#dragLastY = 0;
    this.#dragLastTime = 0;

    if (dialog) dialog.classList.remove('eg-cart-dragging');
  }

  #bindDragScroll() {
    const { dialog } = this.refs;
    if (!dialog || this.#dragBound) return;

    dialog.addEventListener('pointerdown', this.#onPointerDown, {
      capture: true,
      passive: false,
    });
    dialog.addEventListener('pointermove', this.#onPointerMove, {
      capture: true,
      passive: false,
    });
    dialog.addEventListener('pointerup', this.#onPointerUpOrCancel, {
      capture: true,
      passive: true,
    });
    dialog.addEventListener('pointercancel', this.#onPointerUpOrCancel, {
      capture: true,
      passive: true,
    });

    this.#dragBound = true;
  }

  #unbindDragScroll() {
    const { dialog } = this.refs;
    if (!dialog || !this.#dragBound) return;

    this.#stopDragging();
    this.#stopInertia();

    try {
      dialog.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
      dialog.removeEventListener('pointermove', this.#onPointerMove, { capture: true });
      dialog.removeEventListener('pointerup', this.#onPointerUpOrCancel, { capture: true });
      dialog.removeEventListener('pointercancel', this.#onPointerUpOrCancel, { capture: true });
    } catch (_) {}

    this.#dragBound = false;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-eg-cart-open]');
  if (!btn) return;

  event.preventDefault();
  event.stopPropagation();

  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || typeof drawer.open !== 'function') return;

  const header = btn.closest('[data-eg-header]');
  const mobileDrawer = header?.querySelector('[data-eg-drawer]');
  const burger = header?.querySelector('[data-eg-burger]');
  const closeBtn = header?.querySelector('[data-eg-close]');
  const backdrop = header?.querySelector('[data-eg-backdrop]');
  const isMobileMenuOpen = Boolean(header?.classList.contains('is-drawer-open'));

  const openRealCart = () => {
    drawer.open();
  };

  const closeMenuThroughOwnLogic = () => {
    if (!header || !isMobileMenuOpen) return;

    if (closeBtn) {
      closeBtn.click();
      return;
    }

    if (backdrop) {
      backdrop.click();
      return;
    }

    if (burger && header.classList.contains('is-drawer-open')) {
      burger.click();
      return;
    }

    hideMobileMenuUnderCartNow(header, mobileDrawer, burger);
  };

  if (isMobileMenuOpen) {
    openRealCart();

    waitForCartOpenFinish(drawer).then(() => {
      closeMenuThroughOwnLogic();
    });

    return;
  }

  openRealCart();
});
